import { Asset } from 'app-lib/assetUtils'
import { chainConfigs, ChainId, supportedChains } from 'bzx-common/src/config/appConfig'
import hashUtils from 'bzx-common/src/lib/hashUtils'
import * as mobx from 'mobx'

export interface ISettingsStoreProps {
  activePairs: Record<ChainId, { baseToken: Asset; quoteToken: Asset }>
  debugTx: boolean
  devMode: boolean
  favoritePairs: Record<ChainId, string[]>
  fetchRatesOnChain: boolean
  theme: 'light' | 'dark'
  protocolVersionOverride: number
  panelsLayout: 'form-expanded' | 'form-shrinked'
  positionsLayout: 'card' | 'row'
  users: Record<string, { alias: string; avatar: number }>
}

const defaultActivePairs: Record<ChainId, { baseToken: Asset; quoteToken: Asset }> = {
  1: chainConfigs[1].trading.defaultPair,
  10: chainConfigs[10].trading.defaultPair,
  56: chainConfigs[56].trading.defaultPair,
  137: chainConfigs[137].trading.defaultPair,
  42161: chainConfigs[42161].trading.defaultPair,
}

// Copy to be able to restore the protocol version override in the chain configs
const defaultProtocolVersion: Record<ChainId, number> = supportedChains.reduce((acc, chainId) => {
  acc[chainId] = chainConfigs[chainId].protocol.version
  return acc
}, {} as Record<ChainId, number>)

/**
 * Settings for the application
 */
export default class SettingsStore {
  static defaultSettings: ISettingsStoreProps = {
    fetchRatesOnChain: false,
    theme: 'light',
    favoritePairs: supportedChains.reduce((acc, chainId) => {
      acc[chainId] = []
      return acc
    }, {} as Record<ChainId, string[]>),
    activePairs: defaultActivePairs,
    debugTx: false,
    devMode: false,
    protocolVersionOverride: -1,
    panelsLayout: 'form-expanded',
    positionsLayout: 'row',
    users: {} as Record<string, { alias: string; avatar: number }>,
  }

  /**
   * Note the theme setting is not used at the moment, needs app refactoring
   */
  theme = SettingsStore.defaultSettings.theme
  favoritePairs = SettingsStore.defaultSettings.favoritePairs
  /** Whether the app should skip the backend api and fetch lend and borrow rates onchain only */
  fetchRatesOnChain = SettingsStore.defaultSettings.fetchRatesOnChain
  activePairs = SettingsStore.defaultSettings.activePairs
  debugTx = SettingsStore.defaultSettings.debugTx
  devMode = SettingsStore.defaultSettings.devMode
  protocolVersionOverride = SettingsStore.defaultSettings.protocolVersionOverride
  panelsLayout: 'form-expanded' | 'form-shrinked' = 'form-expanded'
  positionsLayout: 'card' | 'row' = 'row'
  users = SettingsStore.defaultSettings.users

  /**
   * @param chainId 1, 42, 56, 137, 42161
   * @param pair ETH/USDT
   */
  addFavoritePair(chainId: ChainId, pair: string) {
    if (!this.favoritePairs[chainId]) {
      // Necessary when we add new chains
      this.favoritePairs[chainId] = []
    }
    this.favoritePairs[chainId].push(pair)
    this.favoritePairs = { ...this.favoritePairs }
  }

  /**
   * @param chainId 1, 42, 56, 137, 42161
   * @param pair ETH/USDT
   */
  removeFavoritePair(chainId: ChainId, pair: string) {
    this.favoritePairs[chainId] = this.favoritePairs[chainId].filter((item) => item !== pair)
    this.favoritePairs = { ...this.favoritePairs }
  }

  /**
   * @param chainId 1, 42, 56, 137
   * @param baseToken ETH
   * @param quoteToken USDT
   */
  setMarketPair(chainId: ChainId, pair: { baseToken: Asset; quoteToken: Asset }) {
    this.activePairs[chainId] = pair
    this.activePairs = { ...this.activePairs }
  }

  /**
   * @param walletAddress 0xa00E002A4e71f886E1082c40322181f022d33000
   * @param name User
   */
  setAlias(walletAddress: string, alias: string) {
    const user = this.users[walletAddress]
    if (!user) {
      // Necessary when we add new user
      this.users[walletAddress] = { alias, avatar: 0 }
    } else {
      this.users[walletAddress].alias = alias
    }
    this.users = { ...this.users }
  }

  /**
   * @param walletAddress 0xa00E002A4e71f886E1082c40322181f022d33000
   * @param avatar 12
   */
  setAvatar(walletAddress: string, avatar: number) {
    if (!this.users[walletAddress]) {
      // Necessary when we add new user
      this.users[walletAddress] = { avatar, alias: hashUtils.shortHash(walletAddress, 4, 4) }
    } else {
      this.users[walletAddress].avatar = avatar
    }

    this.users = { ...this.users }
  }

  switchTheme() {
    this.theme = this.theme === 'light' ? 'dark' : 'light'
  }

  switchLayout() {
    this.panelsLayout = this.panelsLayout === 'form-expanded' ? 'form-shrinked' : 'form-expanded'
  }

  switchPositionsLayout() {
    this.positionsLayout = this.positionsLayout === 'card' ? 'row' : 'card'
  }

  set(prop: keyof ISettingsStoreProps, value: any) {
    ;(this[prop] as any) = value
  }

  assign(props: Partial<ISettingsStoreProps>) {
    Object.assign(this, props)
  }

  /**
   * This will override all chains configs protocol. For more info, check `chainCommon.ts`
   * If -1 is passed it should restore the version from the original config
   */
  setProtocolVersion(version: number) {
    supportedChains.forEach((chainId) => {
      chainConfigs[chainId].protocol.version =
        version === -1 ? defaultProtocolVersion[chainId] : version
    })
    this.protocolVersionOverride = version
  }

  constructor() {
    mobx.makeAutoObservable(this, undefined, { deep: false, autoBind: true })
  }
}
